import type { CSSProperties } from 'react';
import { theme as antdTheme } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { brandFonts } from '@viq-admin/theme';
import { typeScale } from '@viq-admin/ui';
import { TileCaption } from './MetricCard';
import { TILE_NUMBER_GAP, tileLabelStyle } from './tileStyles';

/**
 * Percent of the goal at which the tile turns from red to orange. Below it,
 * red; from it up to (not including) 100, orange; 100 or more, green.
 */
export const SCANS_ON_TRACK_PCT = 70;

export type ScansGoalBand = 'error' | 'warning' | 'success';

interface Props {
  /** The event's scan count (`kpi.total_scans`). Undefined while it is
   *  unknown (loading, or a failed fetch): the tile then shows '—' and makes
   *  no claim about progress. */
  scans: number | undefined;
  /** The event's scans goal. `KpiTiles` renders this card only when it is a
   *  positive number. */
  goal: number;
}

const fmt = (n: number): string => n.toLocaleString('en-US');

/**
 * The Scans tile when the event has a scans goal: Scans │ Goal │ % side by
 * side over a bar that fills toward the goal. The percent, the bar and the
 * caption carry the band's colour (red below 70%, orange 70-99%, green with a
 * check at 100% or more); past the goal the percent keeps counting while the
 * bar stays full. Label, numbers and caption sit at the same heights as every
 * other tile's (`MetricCard`), so the row's numbers line up; the bar runs
 * along the bottom edge.
 *
 * The percent is rounded DOWN, so it never reads as a band it hasn't reached:
 * 174 of 250 (69.6%) shows "69%" in red, and 249 of 250 shows "99%" in
 * orange rather than an orange "100%" with no check.
 */
export function ScansGoalCard({ scans, goal }: Props) {
  const { token } = antdTheme.useToken();
  const known = scans !== undefined;

  // Integer arithmetic so a band edge never lands on the wrong side through
  // floating-point error (29 / 100 * 100 is 28.999…).
  const pct = known ? Math.floor((scans * 100) / goal) : undefined;
  const band: ScansGoalBand | undefined = !known
    ? undefined
    : scans >= goal
      ? 'success'
      : scans * 100 >= SCANS_ON_TRACK_PCT * goal
        ? 'warning'
        : 'error';

  const textColor = band
    ? { success: token.colorSuccessText, warning: token.colorWarningText, error: token.colorErrorText }[band]
    : token.colorTextSecondary;
  const fillColor = band
    ? { success: token.colorSuccess, warning: token.colorWarning, error: token.colorError }[band]
    : undefined;

  const left = known ? goal - scans : 0;
  const caption = !known
    ? null
    : left > 0
      ? `${fmt(left)} below goal`
      : left === 0
        ? 'Goal reached'
        : `${fmt(-left)} over goal`;

  const label = tileLabelStyle(token.colorTextSecondary);
  const number: CSSProperties = {
    fontFamily: brandFonts.headline,
    fontWeight: 700,
    // Scales with the tile's width (container units), so three numbers fit
    // side by side in a laptop-width tile and grow to the usual tile size on
    // a wide screen. The line box stays the usual number height either way,
    // so the numbers sit level with the neighbouring tiles'.
    fontSize: `clamp(17px, 11cqi, ${typeScale.h2}px)`,
    lineHeight: `${typeScale.h2}px`,
    height: typeScale.h2,
    marginTop: TILE_NUMBER_GAP,
    whiteSpace: 'nowrap',
    // Never paint across the divider into the next column; the hover title
    // carries the full numbers.
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
  const column = (first: boolean): CSSProperties => ({
    flex: 1,
    minWidth: 0,
    paddingLeft: first ? 0 : 10,
    marginLeft: first ? 0 : 10,
    borderLeft: first ? undefined : `1px solid ${token.colorBorderSecondary}`,
  });

  const title = known ? `${fmt(scans)} of ${fmt(goal)} scans` : `Goal: ${fmt(goal)} scans`;

  return (
    <div
      data-testid="scans-goal-card"
      data-band={band}
      title={title}
      style={{
        containerType: 'inline-size',
        minHeight: 88,
        padding: '14px 16px',
        borderRadius: token.borderRadius,
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ display: 'flex' }}>
        <div style={column(true)}>
          <div style={label}>Scans</div>
          <div style={{ ...number, color: token.colorText }}>{known ? fmt(scans) : '—'}</div>
        </div>
        <div style={column(false)}>
          <div style={label}>Goal</div>
          <div style={{ ...number, color: token.colorTextTertiary }}>{fmt(goal)}</div>
        </div>
        <div style={column(false)}>
          <div style={label}>%</div>
          <div style={{ ...number, color: band ? textColor : token.colorText }}>
            {pct === undefined ? '—' : `${pct}%`}
            {band === 'success' && (
              <CheckCircleFilled
                aria-hidden
                data-testid="scans-goal-check"
                style={{ fontSize: 16, marginLeft: 6, verticalAlign: 'middle' }}
              />
            )}
          </div>
        </div>
      </div>
      <TileCaption color={textColor}>{caption}</TileCaption>
      <div style={{ marginTop: 'auto', paddingTop: 8 }}>
        <div
          role="progressbar"
          aria-label="Scans toward goal"
          aria-valuemin={0}
          aria-valuemax={100}
          // No value while the count is unknown: an ARIA progressbar without
          // aria-valuenow is indeterminate, rather than claiming 0%.
          aria-valuenow={pct === undefined ? undefined : Math.min(pct, 100)}
          aria-valuetext={known ? `${pct}% of goal, ${title}` : undefined}
          style={{
            height: 6,
            borderRadius: 3,
            background: token.colorFillSecondary,
            overflow: 'hidden',
          }}
        >
          <div
            data-testid="scans-goal-bar"
            style={{
              width: known ? `${Math.min(scans / goal, 1) * 100}%` : '0%',
              height: '100%',
              borderRadius: 3,
              background: fillColor,
              transition: 'width 400ms ease',
            }}
          />
        </div>
      </div>
    </div>
  );
}
